@if (testResult) {
  <h2 mat-dialog-title class="title">
    {{ testResult.name }}
    @if (statusMap.get(testResult.status); as status) {
      <div class="status" [ngClass]="status.class">
        <mat-icon>{{ status.icon }} </mat-icon>
        {{ status.name | translate }}
      </div>
    }
  </h2>
  <mat-dialog-content>
    @if (isVoice) {
      @if (voiceConfigLabel) {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.VOICE_CONFIG' | translate }}</h3>
          <p>{{ voiceConfigLabel }}</p>
        </div>
        <mat-divider></mat-divider>
      }

      @if (testResult.callerInstructions && !testResult.scenario) {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.CALLER_INSTRUCTIONS' | translate }}</h3>
          <p>{{ testResult.callerInstructions }}</p>
        </div>
        <mat-divider></mat-divider>
      }

      @if (testResult.recordingUrl && !audioError) {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.RECORDING' | translate }}</h3>
          <audio controls preload="metadata" [src]="testResult.recordingUrl" (error)="audioError = true"></audio>
        </div>
      } @else {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.RECORDING' | translate }}</h3>
          <p>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.RECORDING_UNAVAILABLE' | translate }}</p>
        </div>
      }

      @if (testResult.chatHistory?.length > 0) {
        <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.TRANSCRIPT' | translate }}</h3>
      }
    }

    <ai-test-chat-history
      [messages]="testResult.chatHistory"
      [testType]="testResult.testType"
    ></ai-test-chat-history>
    <mat-divider></mat-divider>
    @if (hasContext) {
      <div class="invocation-context">
        <h3>{{ 'AI_ASSISTANT.TESTING.CONTEXT.SECTION_LABEL' | translate }}</h3>
        <div class="detail-row">
          <span class="label">{{ 'AI_ASSISTANT.TESTING.CONTEXT.MEDIUM_LABEL' | translate }}</span>
          <span>{{ getMediumName(testResult.invocationContext?.medium) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ 'AI_ASSISTANT.TESTING.CONTEXT.CHANNEL_LABEL' | translate }}</span>
          <span>{{ getChannelName(testResult.invocationContext?.channel) }}</span>
        </div>
        @if (testResult.invocationContext?.authState && testResult.invocationContext.authState !== 'UNSET_AUTH_STATE') {
          <div class="detail-row">
            <span class="label">{{ 'AI_ASSISTANT.TESTING.CONTEXT.AUTH_STATE_LABEL' | translate }}</span>
            <span>{{ getAuthStateName(testResult.invocationContext.authState) }}</span>
          </div>
        }
        @if (testResult.invocationContext?.app && testResult.invocationContext.app !== 'UNSET_APP') {
          <div class="detail-row">
            <span class="label">{{ 'AI_ASSISTANT.TESTING.CONTEXT.APP_LABEL' | translate }}</span>
            <span>{{ getAppName(testResult.invocationContext.app) }}</span>
          </div>
        }
      </div>
      <mat-divider></mat-divider>
    }
    <div class="evaluation">
      @if (testResult.scenario) {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.SCENARIO_LABEL' | translate }}</h3>
          <p>{{ testResult.scenario }}</p>
        </div>
      }
      @if (testResult.reasoning) {
        <div>
          @if (testResult.scenario) {
            <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.REASONING_SUCCESS_EVAL' | translate }}</h3>
          } @else {
            <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.REASONING' | translate }}</h3>
          }
          <p>{{ testResult.reasoning }}</p>
        </div>
      }
      @if (testResult.citations?.length > 0) {
        <div>
          <h3 class="sources">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.SOURCES' | translate }}</h3>
          @for (citation of testResult.citations; track citation) {
            @if (citation.link) {
              <p>{{ citation.title }}</p>
              <a [href]="citation.link" target="_blank"
                >{{ citation.link }}
                <mat-icon inline="true">open_in_new</mat-icon>
              </a>
            }
            @if (citation.fileUrl) {
              <p>{{ citation.title }}</p>
              <a [href]="citation.fileUrl" target="_blank"
                >{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.VIEW_FILE' | translate }}
                <mat-icon inline="true">open_in_new</mat-icon>
              </a>
            }
            <div>{{ citation.reason }}</div>
          }
        </div>
      }

      <div>
        <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.EXPECTATION' | translate }}</h3>
        <p>{{ testResult.expected }}</p>
      </div>

      <div>
        <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.EVALUATION' | translate }}</h3>
        <p>{{ testResult.evaluation }}</p>
      </div>

      @if (testResult.chunks && testResult.chunks.length > 0) {
        <div>
          <h3>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.STREAMING_CHUNKS' | translate }}</h3>
          <ul class="chunk-list">
            @for (chunk of testResult.chunks; track chunk.sequenceNumber) {
              <li>
                <div class="chunk-item" [ngClass]="{ 'error-chunk': chunk.isError }">
                  <div class="chunk-header">
                    @if (chunk.isError) {
                      <span class="chunk-type error">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.ERROR' | translate }}</span>
                    } @else {
                      <span class="chunk-type">{{ getContentTypeName(chunk.contentType) }}</span>
                    }
                    <span class="chunk-sequence"
                      >{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.SEQUENCE' | translate }} {{ chunk.sequenceNumber }}</span
                    >
                    @if (chunk.timestamp) {
                      <span class="chunk-timestamp">{{ chunk.timestamp | date: 'short' }}</span>
                    }
                  </div>
                  @if (chunk.isError) {
                    <div class="chunk-content error">{{ chunk.errorMessage }}</div>
                  } @else {
                    <div class="chunk-content">{{ chunk.content }}</div>
                  }
                </div>
              </li>
            }
          </ul>
        </div>
      }

      @if (expectedCitations?.length > 0) {
        <div>
          <h3 class="sources">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.EXPECTED_CITATIONS' | translate }}</h3>
          @for (citation of expectedCitations; track citation) {
            <div class="expected-citation">
              @if (citation.type && citation.type !== 'UNSPECIFIED') {
                <glxy-badge color="grey" size="small">{{ getCitationTypeName(citation.type) }}</glxy-badge>
              }
              @if (citation.title) {
                <span>{{ citation.title }}</span>
              }
              @if (citation.link) {
                <a [href]="citation.link" target="_blank" rel="noopener noreferrer"
                  >{{ citation.link }}
                  <mat-icon inline="true">open_in_new</mat-icon>
                </a>
              }
            </div>
          }
        </div>
      }

      <div class="tool-calls">
        @if (testResult.expectedToolCalls?.length > 0) {
          <div>
            <h3 class="sources">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.EXPECTED_TOOL_CALLS' | translate }}</h3>
            @for (tool of testResult.expectedToolCalls; track tool) {
              <div class="function">
                <a (click)="openToolCallCitationDetails(tool.name, tool.arguments, tool.response)"
                  >{{ tool.name }}
                  <mat-icon>offline_bolt</mat-icon>
                </a>
              </div>
            }
          </div>
        }

        <div>
          <h3 class="sources">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.TOOLS_CALLED' | translate }}</h3>
          @if (testResult.toolCalls?.length > 0) {
            @for (tool of testResult.toolCalls; track tool) {
              <div class="function">
                <a (click)="openToolCallCitationDetails(tool.toolName, tool.arguments, tool.response)"
                  >{{ tool.toolName }}
                  <mat-icon>offline_bolt</mat-icon>
                </a>
              </div>
            }
          } @else {
            <p>{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.NO_TOOLS_CALLED' | translate }}</p>
          }
        </div>
      </div>
    </div>
  </mat-dialog-content>
}
